import { CONFIG } from '../config';
import { DECOR } from '../content/decor';
import { FLOORS, INTERIOR, interiorDef, WALLS } from '../content/interior';
import { BOOZE_BARN_STOCK, GAS_STATION_STOCK, SCRATCH_ITEMS, type ShopEntry } from '../content/items';
import { RIDES } from '../content/rides';
import { jobsForDay, type JobOffer } from '../content/jobs';
import { HEIRLOOMS, PAYDAY_LOAN } from '../content/pawn';
import type { SfxName } from '../content/types';
import { formatMoney } from '../core/math';
import type { Random } from '../core/Random';
import type { Store } from '../state/Store';
import { hourOf, LOTTO, lottoDraw, lottoWeekDrawn, quickPick } from '../state/rules';
import type { ItemId } from '../state/types';
import { Commerce, type TradeResult } from '../systems/Commerce';
import { fitsIn } from '../world/rooms';
import { clear, h, layer } from './dom';

export type ShopKind =
  'gas' | 'pawn' | 'junkyard' | 'office' | 'bigdave' | 'jobs' | 'decorate' | 'booze' | 'cooter';

export interface ShopHost {
  store: Store;
  rng: Random;
  sfx(name: SfxName): void;
  onOpen(): void;
  onClose(): void;
  acceptJob(offer: JobOffer): void;
  cancelJob(): void;
  onCamaroSold(): void;
  /** You bought a ride, or swapped which one's in your driveway. */
  onRideChanged(): void;
  /** Cooter rolls your new Lil' Rascal out the door. */
  onRascalBought(): void;
  onWin(): void;
  /** What you live in, for what fits inside. */
  homeKind(): 'single' | 'double';
  /** The buyback fund: everyone in the park's contributions, and the goal. */
  fund(): { total: number; goal: number; players: number };
}

const HOURS: Partial<Record<ShopKind, [number, number]>> = {
  pawn: [9, 21],
  junkyard: [7, 19],
  // Blue laws are for other counties.
  booze: [8, 26],
  cooter: [9, 19],
};

/** Is a shop open at this time of day? (No hours means it never closes.) */
export function isShopOpen(kind: ShopKind, minutes: number): boolean {
  const hours = HOURS[kind];
  if (!hours) return true;
  const hour = Math.floor(minutes / 60) % 24;
  return hours[1] > 24 ? hour >= hours[0] || hour < hours[1] - 24 : hour >= hours[0] && hour < hours[1];
}

export function shopHoursText(kind: ShopKind): string {
  const hours = HOURS[kind];
  return hours ? `${clock(hours[0])} to ${clock(hours[1])}` : 'all night';
}

const clock = (h: number): string => {
  const hh = h % 24;
  return `${hh % 12 === 0 ? 12 : hh % 12} ${hh < 12 ? 'AM' : 'PM'}`;
};

export class ShopUI {
  readonly el: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private kind: ShopKind | null = null;
  private note = '';
  private noteOk = true;

  constructor(
    parent: HTMLElement,
    private readonly host: ShopHost,
  ) {
    this.el = layer();
    this.panel = h('div', {
      class: 'modal sheet cardboard',
      attrs: { role: 'dialog', 'aria-modal': 'true' },
    });
    this.el.append(this.panel);
    parent.append(this.el);
    window.addEventListener(
      'keydown',
      (e) => {
        if (!this.kind) return;
        if (e.key === 'Escape' || e.code === 'KeyE') {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.close();
        } else if (['Space', 'KeyQ', 'KeyF', 'KeyR', 'KeyT', 'KeyG', 'KeyP'].includes(e.code)) {
          e.stopImmediatePropagation();
        }
      },
      true,
    );
  }

  get isOpen(): boolean {
    return this.kind !== null;
  }

  open(kind: ShopKind): void {
    if (!this.kind) this.host.onOpen();
    this.kind = kind;
    this.note = '';
    this.el.hidden = false;
    this.render();
  }

  close(): void {
    if (!this.kind) return;
    this.kind = null;
    this.el.hidden = true;
    this.host.onClose();
  }

  private act(r: TradeResult, sfx: SfxName = 'cash'): void {
    this.note = r.message;
    this.noteOk = r.ok;
    this.host.sfx(r.ok ? sfx : 'error');
    this.render();
  }

  private render(): void {
    const kind = this.kind;
    if (!kind) return;
    clear(this.panel);
    const s = this.host.store;
    const hours = HOURS[kind];
    const hour = hourOf(s.minutes);
    // Closing hours past 24 run into the next morning (the Booze Barn closes at 2 AM).
    const closed = hours
      ? hours[1] > 24
        ? hour < hours[0] && hour >= hours[1] - 24
        : hour < hours[0] || hour >= hours[1]
      : false;

    const titles: Record<ShopKind, [string, string]> = {
      gas: [':gas-pump: Gas-N-Grub', 'Open 24/7. The clerk has seen things.'],
      pawn: [":ring: Lucky's Pawn & Payday", 'We buy gold, sell dreams, and cash checks. No questions.'],
      junkyard: [":scrap: Junkyard Jerry's", 'Cash for cans, scrap & car parts.'],
      office: [
        ':trailer: Lucky Acres Office',
        'Rent drop box available 24/7. Darlene is available "sometimes."',
      ],
      bigdave: [":double-wide: Big Dave's Homes", 'Double wides. Single dreams. No credit check.'],
      jobs: [':clipboard: Suds-N-Duds Job Board', 'Honest work for honest(ish) folks.'],
      booze: [':beer: The Booze Barn', 'Cold beer, cheap liquor, lotto. Drive-thru open till 2 AM.'],
      cooter: [":lifted-truck: Cooter's Used Cars", 'We tote the note. Nothing economical. Nothing normal.'],
      decorate: [
        this.host.homeKind() === 'double'
          ? ':couch: Decorate Your Double Wide'
          : ':couch: Decorate Your Single-Wide',
        "Paint, carpet, and what goes where. Buy more stuff at Lucky's Pawn.",
      ],
    };
    const [title, sub] = titles[kind];
    this.panel.append(
      h('button', {
        class: 'close-x',
        text: '✕',
        attrs: { 'aria-label': 'Close' },
        on: { click: () => this.close() },
      }),
      h(
        'div',
        { class: 'sheet-head' },
        h(
          'div',
          {},
          h('div', { class: 'sheet-title', text: title }),
          h('div', { class: 'sheet-sub', text: sub }),
        ),
        h('div', { class: 'wallet', text: formatMoney(s.money) }),
      ),
    );
    if (this.note) {
      this.panel.append(
        h('div', {
          class: 'modal-text',
          text: `${this.noteOk ? ':check:' : ':no:'} ${this.note}`,
          style: { minHeight: '0', marginBottom: '10px' },
        }),
      );
    }
    if (closed && hours) {
      this.panel.append(
        h('div', {
          class: 'modal-text',
          text: `CLOSED. A sign on the door says "GONE FISHIN'." Open ${clock(hours[0])} to ${clock(hours[1])}.`,
        }),
      );
      return;
    }
    switch (kind) {
      case 'gas':
        return this.renderGas();
      case 'pawn':
        return this.renderPawn();
      case 'junkyard':
        return this.renderJunkyard();
      case 'office':
        return this.renderOffice();
      case 'bigdave':
        return this.renderBigDave();
      case 'jobs':
        return this.renderJobs();
      case 'decorate':
        return this.renderDecorate();
      case 'booze':
        return this.renderBooze();
      case 'cooter':
        return this.renderCooter();
    }
  }

  private card(
    icon: string,
    name: string,
    desc: string,
    price: string,
    button: HTMLButtonElement,
    extra?: HTMLElement | null,
  ): HTMLElement {
    return h(
      'div',
      { class: 'item' },
      h(
        'div',
        { class: 'item-top' },
        h('div', { class: 'item-icon', text: icon }),
        h('div', { class: 'item-name', text: name }),
      ),
      h('div', { class: 'item-desc', text: desc }),
      extra ?? null,
      h(
        'div',
        { class: 'item-top', style: { justifyContent: 'space-between' } },
        h('span', { class: 'price', text: price }),
        button,
      ),
    );
  }

  private btn(
    label: string,
    onClick: () => void,
    opts: { disabled?: boolean; primary?: boolean } = {},
  ): HTMLButtonElement {
    return h('button', {
      class: `tape ${opts.primary ? 'primary' : ''}`,
      text: label,
      disabled: opts.disabled,
      on: { click: onClick },
    });
  }

  private stockGrid(stock: readonly ShopEntry[]): HTMLElement {
    const s = this.host.store;
    const grid = h('div', { class: 'grid' });
    for (const e of stock) {
      const id = Object.keys(e.give)[0] as keyof typeof s.state.inv;
      grid.append(
        this.card(
          e.icon,
          e.name,
          e.desc,
          formatMoney(e.price),
          this.btn('Buy', () => this.act(Commerce.buy(s, e)), { disabled: !s.canAfford(e.price) }),
          h('div', { class: 'owned', text: `Have: ${s.state.inv[id]}` }),
        ),
      );
    }
    return grid;
  }

  private renderGas(): void {
    const s = this.host.store;
    this.panel.append(this.stockGrid(GAS_STATION_STOCK));
    if (s.flag('jackpot_day') === s.day)
      this.panel.append(
        h('div', { class: 'section-title', text: ':slots: DOUBLE PRIZE DAY on scratch-offs!' }),
      );
    this.lottoSection();
  }

  private renderBooze(): void {
    this.panel.append(this.stockGrid(BOOZE_BARN_STOCK));
    this.panel.append(h('div', { class: 'section-title', text: ':scratcher: Scratch-Offs' }));
    this.panel.append(
      this.stockGrid(
        GAS_STATION_STOCK.filter((e) => SCRATCH_ITEMS.includes(Object.keys(e.give)[0] as ItemId)),
      ),
    );
    this.lottoSection();
  }

  /** Mega Bucks: pick five numbers (or let the machine), drawing Saturday at 8 PM. */
  private lottoPicks: number[] = [];
  private lottoSection(): void {
    const s = this.host.store;
    const drawn = lottoWeekDrawn(s.day, s.minutes);
    const next = drawn + 1;
    const mine = s.state.lotto.week === next ? s.state.lotto.tickets : [];
    this.panel.append(
      h('div', {
        class: 'section-title',
        text: `:eight-ball: MEGA BUCKS · $${LOTTO.price} · Drawing Saturday 8 PM`,
      }),
      h('div', {
        class: 'item-desc',
        text: `Pick ${LOTTO.picks} numbers from 1 to ${LOTTO.max}. Match 2: $${LOTTO.prizes[2]} · 3: $${LOTTO.prizes[3]} · 4: $${LOTTO.prizes[4]} · ALL 5: $${(LOTTO.prizes[5] ?? 0).toLocaleString()}. Last week's numbers: ${drawn >= 1 ? lottoDraw(drawn).join(' · ') : 'none yet'}.`,
        style: { margin: '0 0 8px' },
      }),
    );
    const grid = h('div', { class: 'lotto-grid' });
    for (let n = 1; n <= LOTTO.max; n++) {
      const on = this.lottoPicks.includes(n);
      grid.append(
        h('button', {
          class: `lotto-ball${on ? ' on' : ''}`,
          text: String(n),
          on: {
            click: () => {
              if (on) this.lottoPicks = this.lottoPicks.filter((x) => x !== n);
              else if (this.lottoPicks.length < LOTTO.picks) this.lottoPicks.push(n);
              this.render();
            },
          },
        }),
      );
    }
    const full = this.lottoPicks.length === LOTTO.picks;
    this.panel.append(
      grid,
      h(
        'div',
        { class: 'choices', style: { flexDirection: 'row', flexWrap: 'wrap', gap: '6px' } },
        this.btn(
          full
            ? `Buy ${[...this.lottoPicks].sort((a, b) => a - b).join('-')}`
            : `Pick ${LOTTO.picks - this.lottoPicks.length} more`,
          () => {
            const r = Commerce.buyLotto(s, this.lottoPicks);
            if (r.ok) this.lottoPicks = [];
            this.act(r, 'ding');
          },
          { disabled: !full || !s.canAfford(LOTTO.price), primary: full },
        ),
        this.btn(
          ':dice: Quick Pick',
          () => {
            const r = Commerce.buyLotto(s, quickPick(this.host.rng));
            this.act(r, 'ding');
          },
          { disabled: !s.canAfford(LOTTO.price) },
        ),
      ),
      h('div', {
        class: 'owned',
        text: mine.length
          ? `Your tickets for Saturday (${mine.length}/${LOTTO.maxTickets}): ${mine.map((t) => t.join('-')).join('  ·  ')}`
          : 'No tickets yet this week.',
        style: { marginTop: '6px' },
      }),
    );
  }

  private renderCooter(): void {
    const s = this.host.store;
    this.panel.append(
      h('div', {
        class: 'modal-text',
        text: "\"Every one of these runs. Most days. What're you lookin' for, somethin' to get you through the mud or somethin' to get you noticed?\"\n\n— Cooter, leaning on a donk",
      }),
    );
    const grid = h('div', { class: 'grid' });
    for (const def of RIDES) {
      const isCamaro = def.id === 'camaro';
      const owned = isCamaro ? !s.flag('camaro_sold') : s.state.rides.includes(def.id);
      if (isCamaro && !owned) continue;
      const parked = s.state.ride === def.id;
      const fixed = !isCamaro || s.state.camaro >= 100;
      const st = def.stats;
      const bars = `Speed ${'▮'.repeat(Math.round(st.maxSpeed / 5))} · Mud ${'▮'.repeat(Math.max(1, Math.round(st.mud * 5)))}`;
      const button = !owned
        ? this.btn(
            'Buy it',
            () => {
              const r = Commerce.buyRide(s, def.id);
              if (r.ok) this.host.onRideChanged();
              this.act(r, 'cash');
            },
            { disabled: !s.canAfford(def.price), primary: s.canAfford(def.price) },
          )
        : this.btn(
            parked ? 'In your driveway' : 'Bring it home',
            () => {
              const r = Commerce.setRide(s, def.id);
              if (r.ok) this.host.onRideChanged();
              this.act(r, 'ding');
            },
            { disabled: parked },
          );
      grid.append(
        this.card(
          def.icon,
          def.name,
          `${def.desc}\n${def.pitch}${isCamaro && !fixed ? ' (Still on blocks.)' : ''}`,
          owned ? 'YOURS' : formatMoney(def.price),
          button,
          h('div', { class: 'owned', text: bars }),
        ),
      );
    }
    this.panel.append(grid);
    // Not a car, technically. Cooter sells it anyway.
    const owned = s.has('rascal');
    this.panel.append(
      h('div', { class: 'section-title', text: ':rascal: Personal Mobility' }),
      h(
        'div',
        { class: 'grid' },
        this.card(
          ':rascal:',
          "Lil' Rascal Mobility Scooter",
          'One previous owner. She was 94 and drove it to the Booze Barn every day for eleven years. Basket, cup holder, a flag so the semis see you, and it goes anywhere you can walk: the porch, the bar, aisle five.\nFaster than Sport Mode. No license. No questions.',
          owned ? 'YOURS' : formatMoney(CONFIG.rascalPrice),
          this.btn(
            owned ? 'Parked outside' : 'Buy it',
            () => {
              const r = Commerce.buyRascal(s, CONFIG.rascalPrice);
              if (r.ok) this.host.onRascalBought();
              this.act(r, 'cash');
            },
            {
              disabled: owned || !s.canAfford(CONFIG.rascalPrice),
              primary: !owned && s.canAfford(CONFIG.rascalPrice),
            },
          ),
          h('div', { class: 'owned', text: 'Speed ▮▮ (▮▮ turbo) · Mud ▮ · Indoors ▮▮▮▮▮' }),
        ),
      ),
    );
  }

  private renderPawn(): void {
    const s = this.host.store;
    this.panel.append(h('div', { class: 'section-title', text: ':moneybag: Pawn Your Stuff' }));
    const heir = h('div', { class: 'grid' });
    const remaining = HEIRLOOMS.filter((x) => s.state.heirlooms.includes(x.id));
    if (!remaining.length)
      heir.append(
        h('div', {
          class: 'item-desc',
          text: "You've pawned everything with sentimental value. Lucky is proud of you.",
        }),
      );
    for (const x of remaining) {
      heir.append(
        this.card(
          x.icon,
          x.name,
          'One of a kind. Like you. (Lucky disagrees.)',
          `+${formatMoney(x.value)}`,
          this.btn('Pawn it', () => this.act(Commerce.sellHeirloom(s, x.id))),
        ),
      );
    }
    this.panel.append(heir);

    this.panel.append(h('div', { class: 'section-title', text: ':flamingo: Yard Stuff (Used, Mostly)' }));
    const decor = h('div', { class: 'grid' });
    for (const d of DECOR.filter((x) => x.buyable)) {
      const owned = s.hasDecor(d.id);
      decor.append(
        this.card(
          d.icon,
          d.name,
          d.desc,
          owned ? 'OWNED' : formatMoney(d.price),
          this.btn(owned ? '✓' : 'Buy', () => this.act(Commerce.buyDecor(s, d.id), 'ding'), {
            disabled: owned || !s.canAfford(d.price),
          }),
          h('div', { class: 'owned', text: `+${d.points} Redneck Rating` }),
        ),
      );
    }
    this.panel.append(decor);

    this.panel.append(
      h('div', { class: 'section-title', text: ':couch: Home Furnishings (Inside Your Place)' }),
    );
    const inside = h('div', { class: 'grid' });
    const kind = this.host.homeKind();
    for (const d of INTERIOR.filter((x) => x.buyable)) {
      const owned = s.hasInterior(d.id);
      const fits = fitsIn(kind, d.id);
      inside.append(
        this.card(
          d.icon,
          d.name,
          `${d.desc}${d.perk ? `\n${d.perk}` : ''}`,
          owned ? 'OWNED' : formatMoney(d.price),
          this.btn(owned ? '✓' : 'Buy', () => this.act(Commerce.buyInterior(s, d.id), 'ding'), {
            disabled: owned || !s.canAfford(d.price),
          }),
          h('div', {
            class: 'owned',
            text: fits ? `+${d.points} Redneck Rating` : `Needs a double wide · +${d.points} Rating`,
          }),
        ),
      );
    }
    this.panel.append(inside);

    this.panel.append(h('div', { class: 'section-title', text: ':credit-card: Payday Loans' }));
    const debt = s.state.debt;
    this.panel.append(
      h(
        'div',
        { class: 'grid' },
        this.card(
          ':cash:',
          `Get $${PAYDAY_LOAN.amount} cash today`,
          `Owe $${PAYDAY_LOAN.owed}, garnished on rent day. ${CONFIG.debtInterest * 100}% weekly interest on anything left. Lucky calls this "a bridge." It's more of a plank.`,
          `+$${PAYDAY_LOAN.amount}`,
          this.btn('Sign here', () => this.act(Commerce.takeLoan(s)), { disabled: debt > 0 }),
        ),
        this.card(
          ':receipt:',
          'Pay off your debt',
          debt > 0 ? `You owe ${formatMoney(debt)}.` : 'You are debt free. Lucky hates this.',
          debt > 0 ? formatMoney(Math.min(debt, s.money)) : '$0.00',
          this.btn('Pay', () => this.act(Commerce.payDebt(s)), { disabled: debt <= 0 || s.money <= 0 }),
        ),
      ),
    );
  }

  private renderJunkyard(): void {
    const s = this.host.store;
    const cans = s.count('cans');
    const scrap = s.count('scrap');
    const camaro = s.state.camaro;
    const grid = h(
      'div',
      { class: 'grid' },
      this.card(
        ':empty-can:',
        `Sell ${cans} can${cans === 1 ? '' : 's'}`,
        `$${CONFIG.canValue.toFixed(2)} each. Crush 'em first for style points.`,
        `+${formatMoney(cans * CONFIG.canValue)}`,
        this.btn('Sell', () => this.act(Commerce.sellCans(s)), { disabled: cans === 0 }),
      ),
      this.card(
        ':scrap:',
        `Sell ${scrap} scrap`,
        '$7-$14 a piece, depending on how Jerry feels about it.',
        scrap ? '~$' + scrap * 10 : '$0',
        this.btn('Sell', () => this.act(Commerce.sellScrap(s, this.host.rng)), { disabled: scrap === 0 }),
      ),
      this.card(
        ':copper-wire:',
        `Sell ${s.count('copper')} lb of copper`,
        `$${CONFIG.copperPerLb[0]}-$${CONFIG.copperPerLb[1]} a pound. Strip it out of the condemned trailers in the Back Forty.`,
        s.count('copper') ? `~$${s.count('copper') * 5}` : '$0',
        this.btn('Sell', () => this.act(Commerce.sellCopper(s, this.host.rng)), {
          disabled: s.count('copper') === 0,
        }),
      ),
      this.card(
        ':gear:',
        'Camaro Part',
        `Each part + an hour of work = ${Math.round(100 / CONFIG.camaroPartsNeeded)}% closer to a running Camaro. You have ${s.count('parts')}.`,
        formatMoney(CONFIG.partPrice),
        this.btn('Buy', () => this.act(Commerce.buyPart(s)), {
          disabled: camaro >= 100 || !s.canAfford(CONFIG.partPrice),
        }),
      ),
    );
    if (camaro >= 100 && !s.flag('camaro_sold')) {
      grid.append(
        this.card(
          ':car:',
          "Sell Jerry the '87 Camaro",
          "She runs. Jerry wants her bad. Can't un-sell her.",
          `+${formatMoney(CONFIG.camaroSaleValue)}`,
          this.btn('Sell her', () => {
            const r = Commerce.sellCamaro(s);
            if (r.ok) this.host.onCamaroSold();
            this.act(r);
          }),
        ),
      );
    }
    this.panel.append(grid);
  }

  private renderOffice(): void {
    const s = this.host.store;
    const r = s.rentStatus();
    const rules = [
      'Lot rent due FRIDAYS by 5 PM. Three missed weeks and you are OUT.',
      'No more than 3 vehicles on cinder blocks per lot.',
      'No fireworks. (Everyone ignores this.)',
      'Quiet hours 10 PM to 7 AM. (Everyone ignores this.)',
      'Pets must be leashed. Possums count as pets once named.',
      'There is no Lot 13. Do not ask about Lot 13.',
    ];
    if (s.flag('apex'))
      rules.push('NEW: Rent increases weekly per Apex Capital Holdings LLC "market adjustments."');
    this.panel.append(
      h(
        'div',
        { class: 'grid' },
        this.card(
          ':receipt:',
          `Week ${r.week} lot rent`,
          r.paid
            ? 'Paid. Darlene stamped it with a smiley face.'
            : `Due ${r.daysUntil <= 0 ? 'today' : `in ${r.daysUntil} day(s)`} at 5 PM. Strikes: ${s.state.rent.strikes}/${CONFIG.maxStrikes}.`,
          r.paid ? 'PAID' : formatMoney(r.amount),
          this.btn(
            r.paid ? '✓ Paid' : 'Drop in box',
            () => {
              if (s.payRent())
                this.act({
                  ok: true,
                  message: 'You slide the envelope into the drop box. It makes a sad little thunk.',
                });
              else
                this.act({
                  ok: false,
                  message: 'Not enough cash. Darlene does not accept IOUs, Venmo, or "vibes."',
                });
            },
            { disabled: r.paid || !s.canAfford(r.amount), primary: !r.paid },
          ),
        ),
        this.fundCard(),
        h(
          'div',
          { class: 'item' },
          h('div', { class: 'item-name', text: ':scroll: Park Rules (posted)' }),
          h('div', {
            class: 'item-desc',
            text: rules.map((x) => `• ${x}`).join('\n'),
            style: { whiteSpace: 'pre-wrap' },
          }),
        ),
      ),
    );
  }

  /** The jar on Darlene's desk: buy Lucky Acres back from Apex Capital. */
  private fundCard(): HTMLElement {
    const s = this.host.store;
    const f = this.host.fund();
    const owned = !!s.flag('park_owned');
    const pct = Math.min(100, (f.total / f.goal) * 100);
    const who = f.players > 1 ? `the ${f.players} of you` : 'you';
    const desc = owned
      ? 'Lucky Acres belongs to the residents now. Rent is just co-op dues. Brent from Apex cried in his Tesla.'
      : `Apex Capital will sell the park back for $${f.goal.toLocaleString()}. So far ${who} put in $${Math.round(f.total).toLocaleString()} (you: $${Math.round(s.state.fund).toLocaleString()}).`;
    const amounts = [50, 250, 1000];
    const row = h(
      'div',
      { class: 'choices', style: { flexDirection: 'row', flexWrap: 'wrap', gap: '6px' } },
      ...amounts.map((a) =>
        this.btn(`+$${a.toLocaleString()}`, () => this.act(Commerce.contributeFund(s, a)), {
          disabled: owned || s.money < a,
        }),
      ),
    );
    return h(
      'div',
      { class: 'item' },
      h('div', {
        class: 'item-name',
        text: owned
          ? ':trophy: Resident-Owned Since Day ' + String(s.flag('park_owned'))
          : ':jar: Buy Back the Park',
      }),
      h('div', { class: 'item-desc', text: desc }),
      h('div', { class: 'progress' }, h('div', { style: { width: `${pct}%` } })),
      owned ? null : row,
    );
  }

  private renderDecorate(): void {
    const s = this.host.store;
    const kind = this.host.homeKind();
    const inside = s.state.interior;
    const finishGrid = (which: 'walls' | 'floor'): HTMLElement => {
      const grid = h('div', { class: 'grid' });
      for (const f of which === 'walls' ? WALLS : FLOORS) {
        const current = inside[which] === f.id;
        const owned = f.price === 0 || !!s.flag(`finish_${which}_${f.id}`);
        grid.append(
          this.card(
            which === 'walls' ? ':paint:' : ':yarn:',
            f.name,
            owned ? 'Yours. Switch any time.' : 'Installed same day by a guy named Rusty.',
            current ? 'CURRENT' : owned ? 'OWNED' : formatMoney(f.price),
            this.btn(
              current ? '✓' : owned ? 'Use' : 'Buy',
              () => this.act(Commerce.setFinish(s, which, f.id), 'ding'),
              {
                disabled: current || (!owned && !s.canAfford(f.price)),
              },
            ),
          ),
        );
      }
      return grid;
    };
    this.panel.append(h('div', { class: 'section-title', text: ':paint: Walls' }), finishGrid('walls'));
    this.panel.append(h('div', { class: 'section-title', text: ':yarn: Floors' }), finishGrid('floor'));
    this.panel.append(h('div', { class: 'section-title', text: ':box: Your Stuff' }));
    const grid = h('div', { class: 'grid' });
    if (!inside.items.length)
      grid.append(
        h('div', {
          class: 'item-desc',
          text: "Nothing yet. Lucky's Pawn has home furnishings, and the beer cans are piling up somewhere.",
        }),
      );
    for (const id of inside.items) {
      const d = interiorDef(id);
      if (!d) continue;
      const stored = inside.stored.includes(id);
      const fits = fitsIn(kind, id);
      grid.append(
        this.card(
          d.icon,
          d.name,
          fits
            ? (d.perk ?? d.desc)
            : 'No room for it in a single-wide. It goes out when you get the double wide.',
          stored ? 'IN THE CLOSET' : fits ? 'ON DISPLAY' : 'WAITING',
          this.btn(stored ? 'Put it out' : 'Put it away', () => {
            s.setStored(id, !stored);
            this.act(
              { ok: true, message: stored ? `${d.name} is back out.` : `${d.name} went in the closet.` },
              'ding',
            );
          }),
        ),
      );
    }
    this.panel.append(grid);
  }

  private renderBigDave(): void {
    const s = this.host.store;
    if (s.flag('won')) {
      this.panel.append(
        h('div', {
          class: 'modal-text',
          text: '"You already bought one, chief! Delivery\'s 6 to 8 weeks. Maybe 10. Depends on the river." Big Dave hands you a free koozie.',
        }),
      );
      return;
    }
    const price = CONFIG.doubleWidePrice;
    const pct = Math.min(100, (s.money / price) * 100);
    const can = Commerce.canBuyDoubleWide(s);
    this.panel.append(
      h(
        'div',
        { class: 'modal-text' },
        `"Lookit this beauty. 1,400 square feet. TWO bathrooms. A pitched roof like a real house! $${price.toLocaleString()} down and she's yours, chief. Your credit score? Don't care. Didn't ask. Won't ask."\n\n— Big Dave, probably sweating`,
      ),
      h('div', {
        class: 'section-title',
        text: `Your down payment: ${formatMoney(s.money)} / $${price.toLocaleString()}`,
      }),
      h('div', { class: 'progress' }, h('div', { style: { width: `${pct}%` } })),
      h(
        'div',
        { class: 'choices' },
        this.btn(
          can ? ':writing: Sign the papers. Make it a double.' : `Need ${formatMoney(price - s.money)} more`,
          () => {
            const r = Commerce.buyDoubleWide(s);
            if (r.ok) {
              this.close();
              this.host.onWin();
            } else this.act(r);
          },
          { disabled: !can, primary: true },
        ),
      ),
    );
  }

  private renderJobs(): void {
    const s = this.host.store;
    const job = s.state.job;
    if (job) {
      this.panel.append(
        h('div', { class: 'section-title', text: ':hammer: Current Job' }),
        h(
          'div',
          { class: 'grid' },
          this.card(
            ':clipboard:',
            job.title,
            'In progress. Your future boss is watching. (Not really.)',
            `$${job.pay}`,
            this.btn('Quit job', () => {
              this.host.cancelJob();
              this.render();
            }),
          ),
        ),
      );
    }
    this.panel.append(
      h('div', { class: 'section-title', text: `:pushpin: Today's Odd Jobs (Day ${s.day})` }),
    );
    const grid = h('div', { class: 'grid' });
    for (const offer of jobsForDay(s.state.seed, s.day)) {
      const taken = !!s.flag(`job_taken_${offer.id}`);
      grid.append(
        this.card(
          offer.kind === 'mow' ? ':sprout:' : ':box-truck:',
          offer.title,
          `${offer.desc} Time limit: ${Math.round(offer.minutes / 60)}h.`,
          `$${offer.pay}`,
          this.btn(
            taken ? 'Taken' : 'Take it',
            () => {
              this.host.acceptJob(offer);
              this.close();
            },
            { disabled: taken || !!job, primary: !taken && !job },
          ),
        ),
      );
    }
    this.panel.append(grid);
  }
}
